import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Button, { ButtonVariant } from "common/basicComponents/Button";
import Icon, { IconName } from "common/basicComponents/Icon";
import Tooltip from "common/basicComponents/Tooltip";
import Typography, {
  TypographyVariant,
  TypographyWeight,
} from "common/basicComponents/Typography";

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border: 1px solid
    ${({ theme, active }) =>
      active ? theme.colors.green : theme.colors.lightGrey};
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
  background: ${({ theme }) => theme.colors.white};
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const ActiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: ${({ theme }) => theme.colors.green};
  font-weight: 600;
`;

const LockedAnchor = styled.span`
  display: inline-flex;
`;

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const pad = (n) => String(n).padStart(2, "0");

const CardListItem = ({ card, onSetActive, onEdit, onDelete, disableDelete }) => {
  const { t } = useTranslation("billing");

  return (
    <Row active={card.isActive}>
      <Info>
        <Typography
          variant={TypographyVariant.BODY1}
          weight={TypographyWeight.BOLD}
          text={`${capitalize(card.brand)} •••• ${card.last4}`}
        />
        <Typography
          variant={TypographyVariant.CAPTION}
          text={`${t("expDate")}: ${pad(card.exp_month)}/${card.exp_year}`}
        />
        {card.billing_details && card.billing_details.name && (
          <Typography
            variant={TypographyVariant.CAPTION}
            text={card.billing_details.name}
          />
        )}
      </Info>
      <Actions>
        {card.isActive ? (
          <ActiveBadge>
            <Icon name={IconName.CHECKMARK} size="1rem" />
            {t("active")}
          </ActiveBadge>
        ) : (
          <Button
            variant={ButtonVariant.OUTLINED}
            text={t("setActive")}
            onClick={() => onSetActive(card.id)}
          />
        )}
        <Button
          variant={ButtonVariant.OUTLINED}
          icon={IconName.EDIT}
          onClick={() => onEdit(card)}
        />
        {disableDelete ? (
          <>
            <LockedAnchor className="card-delete-locked">
              <Button
                variant={ButtonVariant.ERROR}
                icon={IconName.TRASH}
                disabled
              />
            </LockedAnchor>
            <Tooltip
              anchorSelect=".card-delete-locked"
              text={t("activeCardLocked")}
            />
          </>
        ) : (
          <Button
            variant={ButtonVariant.ERROR}
            icon={IconName.TRASH}
            onClick={() => onDelete(card)}
          />
        )}
      </Actions>
    </Row>
  );
};

CardListItem.propTypes = {
  card: PropTypes.shape({
    id: PropTypes.string.isRequired,
    brand: PropTypes.string,
    last4: PropTypes.string,
    exp_month: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    exp_year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isActive: PropTypes.bool,
    billing_details: PropTypes.object,
  }).isRequired,
  onSetActive: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  disableDelete: PropTypes.bool,
};

CardListItem.defaultProps = {
  disableDelete: false,
};

export default CardListItem;
